/**
 * Compact social-style counts (Instagram follower style).
 *
 * Exact under 10,000 (max 4 digits, with grouping separators). Larger values
 * use K / M / B with at most 4 significant digits:
 *
 *   123      → "123"
 *   1,234    → "1,234"
 *   12,300   → "12.3K"
 *   123,400  → "123.4K"
 *   1,234,000 → "1.234M"
 *
 * Compact suffixes are always English (K/M/B) so EN and ZH UI stay consistent.
 * `locale` only affects grouping of the exact (sub-10k) form.
 */

const COMPACT_THRESHOLD = 10_000;

const compactFormatter = new Intl.NumberFormat('en', {
  notation: 'compact',
  compactDisplay: 'short',
  maximumSignificantDigits: 4,
});

function toCount(value: number | null | undefined): number {
  if (value == null || !Number.isFinite(value) || value < 0) return 0;
  return Math.round(value);
}

/** Exact count with grouping separators, e.g. 123232 → "123,232". */
export function formatCountExact(
  value: number | null | undefined,
  locale = 'en-US',
): string {
  return toCount(value).toLocaleString(locale);
}

export function formatCount(
  value: number | null | undefined,
  locale = 'en-US',
): string {
  const n = toCount(value);
  if (n < COMPACT_THRESHOLD) return n.toLocaleString(locale);
  return compactFormatter.format(n);
}
